<script setup lang="ts">
import { z } from "zod";
import { type IOurRecaptchaComposition, useVueRecaptcha } from "~/composables/useVueRecaptcha";
import type { CountryRecord } from "~/graphql/datocms/generated";
import type { FormState } from "~/models/forms";

const { locale, t } = useI18n();

definePageMeta({
  layout: "anonymous",
  auth: {
    anonymousOnly: true,
    redirectAuthenticatedTo: "/",
  },
});

useHead({
  title: () => t("register.title"),
});

// only on client, we don't need on server render
const page = import.meta.client && window?.location.href;

const { data: countryData } = await useFetch<Array<CountryRecord>>("/api/cms/countries", {
  query: { locale: locale },
});

const countries = countryData.value?.map((c: CountryRecord) => {
  return { code: c.code, name: c.name };
});

const loadingIndicator = useLoadingIndicator();
const gtm = useGtmTracking();
const formState = ref<FormState>("mounting");
const state = reactive({
  companyName: undefined,
  vatNumber: undefined,
  firstName: undefined,
  name: undefined,
  emailAddress: undefined,
  country: countries?.at(0)?.code,
  street: undefined,
  houseNumber: undefined,
  postalCode: undefined,
  city: undefined,
  phoneNumber: undefined,
  languageCode: locale.value,
  pageUrl: page,
});

const formValidationEmail = t("form.validation.email");
const formValidationRequired = t("form.validation.required");

const registrationFormSchema = z.object({
  companyName: z.string({ required_error: formValidationRequired }),
  vatNumber: z.string({ required_error: formValidationRequired }),
  firstName: z.string({ required_error: formValidationRequired }),
  name: z.string({ required_error: formValidationRequired }),
  emailAddress: z.string({ required_error: formValidationRequired }).email(formValidationEmail),
  country: z.string({ required_error: formValidationRequired }),
  street: z.string({ required_error: formValidationRequired }),
  houseNumber: z.string({ required_error: formValidationRequired }),
  postalCode: z.string({ required_error: formValidationRequired }),
  city: z.string({ required_error: formValidationRequired }),
  phoneNumber: z.string({ required_error: formValidationRequired }),
});

let recaptcha: IOurRecaptchaComposition | undefined;

async function onSubmit() {
  formState.value = "pending";
  loadingIndicator.start();

  try {
    const recaptchaToken = (recaptcha && await recaptcha.execute("register")) || "";

    await $fetch("/api/ecom/account/register", {
      method: "POST",
      body: unref(state),
      headers: { "X-Recaptcha-Token": recaptchaToken },
    });

    gtm.trackCustomEvent("registration-submit");

    formState.value = "success";
    document.body.scrollIntoView({ behavior: "smooth" });
  }
  catch (error: any) {
    formState.value = "error";
    useFailToast(t("common.title.error"), error.toString());
  }
  finally {
    loadingIndicator.finish();
  }
}

onMounted(async () => {
  recaptcha = await useVueRecaptcha();
  formState.value = "idle";
});
</script>

<template>
  <CenterCard :title="t('register.title')">
    <template v-if="formState !== 'success'">
      <p>
        {{ $t("register.info") }}
      </p>

      <p>{{ $t("register.benefits") }}:</p>

      <CheckList class="mb-4xl">
        <CheckListItem :label="t('register.benefits.1')" />
        <CheckListItem :label="t('register.benefits.2')" />
        <CheckListItem :label="t('register.benefits.3')" />
      </CheckList>

      <UForm :state="state" :schema="registrationFormSchema" @submit="onSubmit">
        <div class="grid w-full grid-cols-12 gap-x-xl sm:w-[368px]">
          <UFormGroup
            :label="t('register.label.company')" name="companyName" required class="col-span-12 sm:col-span-10"
          >
            <UInput v-model="state.companyName" type="text" autocomplete="organization" data-1p-ignore />
          </UFormGroup>

          <UFormGroup :label="t('register.label.vat')" name="vatNumber" required class="col-span-12 sm:col-span-10">
            <UInput v-model="state.vatNumber" type="text" data-1p-ignore />
          </UFormGroup>

          <UFormGroup :label="t('register.label.first-name')" name="firstName" required class="col-span-6">
            <UInput v-model="state.firstName" type="text" autocomplete="given-name" data-1p-ignore />
          </UFormGroup>

          <UFormGroup :label="t('register.label.last-name')" name="name" required class="col-span-6">
            <UInput v-model="state.name" type="text" autocomplete="family-name" data-1p-ignore />
          </UFormGroup>

          <UFormGroup
            :label="t('register.label.email')" name="emailAddress" required class="col-span-12 sm:col-span-10"
          >
            <UInput v-model="state.emailAddress" type="email" autocomplete="email" inputmode="email" data-1p-ignore />
          </UFormGroup>

          <UFormGroup :label="t('register.label.country')" name="country" required class="col-span-12 sm:col-span-7">
            <USelectMenu v-model="state.country" :options="countries" option-attribute="name" value-attribute="code" />
          </UFormGroup>

          <UFormGroup :label="t('register.label.street')" name="street" required class="col-span-9">
            <UInput v-model="state.street" type="text" autocomplete="address-line1" data-1p-ignore />
          </UFormGroup>

          <UFormGroup :label="t('register.label.number')" name="houseNumber" required class="col-span-3">
            <UInput v-model="state.houseNumber" type="text" autocomplete="address-line2" data-1p-ignore />
          </UFormGroup>

          <UFormGroup :label="t('register.label.postal-code')" name="postalCode" required class="col-span-5">
            <UInput v-model="state.postalCode" type="text" autocomplete="postal-code" data-1p-ignore />
          </UFormGroup>

          <UFormGroup :label="t('register.label.city')" name="city" required class="col-span-7">
            <UInput v-model="state.city" type="text" autocomplete="address-level2" data-1p-ignore />
          </UFormGroup>

          <UFormGroup :label="t('register.label.phone')" name="phoneNumber" required class="col-span-12 sm:col-span-8">
            <UInput v-model="state.phoneNumber" type="text" autocomplete="tel" inputmode="tel" data-1p-ignore />
          </UFormGroup>

        <!-- <UFormGroup label="Example of optional field" hint="(optionnel)" :ui="optionalConfig" class="col-span-12">
          <UInput id="example" type="text" autocomplete="example" />
        </UFormGroup> -->
        </div>

        <UButton
          type="submit"
          class="mb-4xl w-full justify-center sm:w-auto sm:justify-start"
          :loading="formState === 'pending'"
          :disabled="formState === 'mounting'"
        >
          {{ $t('register.action.submit') }}
        </UButton>
      </UForm>
      <AlreadyAnAccount />
    </template>
    <template v-if="formState === 'success'">
      <p class="mb-4xl">
        {{ $t("register.message.success") }}
      </p>
    </template>
  </CenterCard>
</template>
